import React, { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquarePlus, RefreshCw, Share2 } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SupportSection from "./SupportSection";
import TicketAnalysisPanel from "./TicketAnalysisPanel";
import { getPatientData } from "./patientApi";
import { getSupportTicket, getSharedSession } from "./ticketApi";
import { chatSurfaceClasses, chatSurfaceStyle } from "./chatBackground";
import { DoctorCard, PatientCard, TicketSummaryCard } from "./ticketCards";
import { supportHomePath } from "./supportUrls";
import { formatTicketTimestamp, htmlToPlainText } from "./ticketUtils";
import FeedbackForm from "./FeedbackForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";

const SENDER_PALETTE = [
  { name: "text-violet-800" },
  { name: "text-sky-800" },
  { name: "text-emerald-800" },
  { name: "text-rose-800" },
  { name: "text-amber-800" },
  { name: "text-indigo-800" },
  { name: "text-teal-800" },
];

const senderPalette = (key) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return SENDER_PALETTE[hash % SENDER_PALETTE.length];
};

// Lowercase, strip "Dr."/"MD" honorifics and punctuation so "Dr. Mario
// Pineda" matches "Mario Pineda".
const normalizeSenderName = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/\b(dr|md|do)\.?\b/g, " ")
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * WhatsApp-style bubble: the requester's (doctor's) messages sit on the
 * left; CS/internal senders sit on the right. Each unique sender gets a
 * stable color so multi-party threads stay readable.
 */
const ConversationMessage = ({
  message,
  requesterEmail,
  requesterName,
  doctorEmail,
  doctorName,
}) => {
  const text =
    message.body_text?.trim() || htmlToPlainText(message.body_html) || "(empty message)";
  const sentAt = formatTicketTimestamp(message.created_at);
  const senderEmail = message.sender?.email?.toLowerCase() || null;
  const senderLabel =
    message.sender?.name || message.sender?.email || "Unknown sender";

  // The requester is identified by (in order of reliability): email match,
  // Pylon's external author types, then name match — Pylon message authors
  // often carry only a display name.
  const senderName = normalizeSenderName(message.sender?.name);
  const nameMatches = (candidate) => {
    const normalized = normalizeSenderName(candidate);
    return Boolean(senderName && normalized && senderName === normalized);
  };
  const isDoctorSide =
    (senderEmail && requesterEmail && senderEmail === requesterEmail.toLowerCase()) ||
    (senderEmail && doctorEmail && senderEmail === doctorEmail.toLowerCase()) ||
    ["contact", "customer", "external"].includes(
      String(message.sender?.type || "").toLowerCase()
    ) ||
    nameMatches(requesterName) ||
    nameMatches(doctorName);

  const palette = senderPalette(
    (senderEmail || senderLabel).toLowerCase()
  );

  const bubbleClasses = isDoctorSide
    ? "bg-[#F4F1FF] border-[#C7B9FF]"
    : "bg-white border-gray-200";

  return (
    <div className={`flex ${isDoctorSide ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[50%] rounded-xl border px-3.5 py-2.5 shadow-sm ${bubbleClasses} ${
          message.is_internal ? "border-dashed" : ""
        }`}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 mb-1">
          <p
            className={`min-w-0 text-[14px] font-semibold ${
              isDoctorSide ? "font-medium text-[#4C1D95]" : `font-semibold ${palette.name}`
            }`}
          >
            {senderLabel}
            {message.is_internal && (
              <span className="ml-2 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                Internal note
              </span>
            )}
          </p>
          {sentAt && (
            <p
              className={`text-[11px] shrink-0 tabular-nums ${
                isDoctorSide ? "text-[#6B5A7A]" : "text-gray-500"
              }`}
              title={sentAt}
            >
              {sentAt}
            </p>
          )}
        </div>
        <p
          className={`text-[16px] leading-relaxed whitespace-pre-wrap break-words ${
            isDoctorSide ? "text-gray-900" : "text-gray-800"
          }`}
        >
          {text}
        </p>
      </div>
    </div>
  );
};

const SharedSessionBanner = ({ share }) => {
  if (share?.loading) {
    return (
      <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
        Loading shared session…
      </div>
    );
  }
  if (share?.error) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
        {share.error}
      </div>
    );
  }
  if (share?.data) {
    const when = share.data.created_at
      ? new Date(share.data.created_at).toLocaleString()
      : "unknown time";
    return (
      <div className="rounded-md bg-purple-50 border border-purple-200 px-4 py-3 text-sm text-purple-800 flex items-start gap-2">
        <Share2 className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
        <span>
          <span className="font-medium">Shared session</span> — opened by{" "}
          <span className="font-semibold">{share.data.shared_by || "someone"}</span>{" "}
          on {when}. You are on your own copy; changes won't affect the original.
        </span>
      </div>
    );
  }
  return null;
};

const SupportTicketPage = () => {
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const shareId = searchParams.get("share");

  const [bundle, setBundle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  // Shared session restore
  const [share, setShare] = useState(shareId ? { loading: true } : null);

  // Patients/doctors shown in the "Doctor & Patient Info" section. Fed by
  // the analysis panel selections plus patients the agent resolves live.
  const [selection, setSelection] = useState({ patientIds: [], doctors: [] });
  const [agentPatientIds, setAgentPatientIds] = useState([]);
  // Cache of loaded patient records: { [patientId]: { loading, patient } }
  const [patientRecords, setPatientRecords] = useState({});

  const handlePatientResolved = useCallback((patientId) => {
    if (!patientId) return;
    const id = String(patientId);
    setAgentPatientIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const handleSelectionChange = useCallback((next) => {
    setSelection({
      patientIds: (next?.patientIds || []).map(String),
      doctors: next?.doctors || [],
    });
  }, []);

  const displayedPatientIds = [
    ...new Set([...selection.patientIds, ...agentPatientIds]),
  ];

  // Fetch any displayed patient not yet in the cache.
  useEffect(() => {
    const missing = displayedPatientIds.filter((id) => !patientRecords[id]);
    if (missing.length === 0) return;

    setPatientRecords((prev) => {
      const next = { ...prev };
      for (const id of missing) next[id] = { loading: true, patient: null };
      return next;
    });

    missing.forEach((id) => {
      getPatientData(id).then((data) => {
        setPatientRecords((prev) => ({
          ...prev,
          [id]: {
            loading: false,
            patient:
              data && !data.error && Object.keys(data).length > 0
                ? data
                : null,
          },
        }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedPatientIds.join(",")]);

  const loadTicket = useCallback(async (id, { refreshing = false } = {}) => {
    if (refreshing) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setLookupError(null);
    try {
      const data = await getSupportTicket(id);
      if (data && !data.error && data.ticket) {
        setBundle(data);
      } else {
        if (!refreshing) setBundle(null);
        setLookupError(data?.error || "Ticket not found.");
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (ticketId) {
      loadTicket(ticketId);
    }
  }, [ticketId, loadTicket]);

  // Hydrate shared session from DynamoDB when the URL has ?share=<id>
  useEffect(() => {
    if (!shareId) return;
    let cancelled = false;
    (async () => {
      const data = await getSharedSession(shareId);
      if (cancelled) return;
      if (data.error) {
        setShare({ error: data.error });
        return;
      }
      setShare({ data });
      if (data.ui?.patient_id) handlePatientResolved(data.ui.patient_id);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);

  if (isLoading && !bundle) {
    return (
      <div className="flex flex-col w-full max-w-5xl mx-auto">
        <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>
        <p className="mt-6 text-sm text-gray-500">Loading ticket...</p>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="flex flex-col w-full max-w-5xl mx-auto">
        <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>
        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {lookupError || "Ticket not found."}
        </p>
        <Link
          to={supportHomePath()}
          className="mt-4 text-ktx-purple hover:underline font-medium text-sm w-fit"
        >
          Back to search
        </Link>
      </div>
    );
  }

  const { ticket, messages, requester, doctor } = bundle;

  // Doctors shown in the info section: the requester match plus CS selections.
  const displayedDoctors = [];
  {
    const seen = new Set();
    for (const d of [doctor, ...selection.doctors]) {
      if (!d || seen.has(String(d.user_id))) continue;
      seen.add(String(d.user_id));
      displayedDoctors.push(d);
    }
  }

  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto pb-12">
      <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-gray-300 bg-slate-50 px-3 py-2 text-sm shadow-sm">
        <p className="text-gray-800 truncate min-w-0">
          <span className="font-medium">
            Pylon Ticket #{ticket.number ?? ticket.id}
          </span>
          <span className="text-gray-500"> · {ticket.title}</span>
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => loadTicket(ticketId, { refreshing: true })}
            disabled={isRefreshing}
            className="inline-flex items-center justify-center rounded-md p-1.5 text-ktx-purple hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-ktx-purple disabled:opacity-50"
            title="Reload ticket"
            aria-label="Reload ticket"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              aria-hidden
            />
          </button>
          <Link
            to={supportHomePath()}
            className="text-ktx-purple hover:underline font-medium"
          >
            Change ticket
          </Link>
        </div>
      </div>

      {lookupError && (
        <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {lookupError}
        </p>
      )}

      <div className="mt-3 space-y-4">
        <SupportSection
          title="Pylon Ticket"
          summary={`#${ticket.number ?? ticket.id} · ${ticket.state}`}
          defaultOpen
        >
          <div className="space-y-3">
            <TicketSummaryCard ticket={ticket} requester={requester} />

            <SupportSection
              title="Conversation"
              summary={`${messages.length} message${messages.length === 1 ? "" : "s"}`}
              contentClassName="px-3 py-3"
            >
              {messages.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No messages found for this ticket.
                </p>
              ) : (
                <div
                  className={`${chatSurfaceClasses} space-y-2.5 max-h-[32rem] overflow-y-auto p-3`}
                  style={chatSurfaceStyle}
                >
                  {messages.map((message, index) => (
                    <ConversationMessage
                      key={message.id || index}
                      message={message}
                      requesterEmail={
                        requester?.email || ticket.requester_email
                      }
                      requesterName={requester?.name}
                      doctorEmail={doctor?.user_email}
                      doctorName={doctor?.user_name}
                    />
                  ))}
                </div>
              )}
            </SupportSection>
          </div>
        </SupportSection>

        <SupportSection
          title="Doctor & Patient Info"
          summary={[
            `${displayedDoctors.length} doctor${displayedDoctors.length === 1 ? "" : "s"}`,
            displayedPatientIds.length > 0 &&
              `${displayedPatientIds.length} patient${displayedPatientIds.length === 1 ? "" : "s"}`,
          ]
            .filter(Boolean)
            .join(" · ")}
          defaultOpen
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
            {displayedDoctors.length === 0 && (
              <DoctorCard doctor={null} requester={requester} />
            )}
            {displayedDoctors.map((d) => (
              <DoctorCard key={d.user_id} doctor={d} requester={requester} />
            ))}
            {displayedPatientIds.map((pid) => (
              <PatientCard
                key={pid}
                patientId={pid}
                patient={patientRecords[pid]?.patient || null}
                isLoading={patientRecords[pid]?.loading || false}
              />
            ))}
          </div>
        </SupportSection>

        <SharedSessionBanner share={share} />

        {!share?.loading && (
          <TicketAnalysisPanel
            key={share?.data?.session_id || "own"}
            ticketId={ticketId}
            doctor={doctor}
            onPatientResolved={handlePatientResolved}
            onSelectionChange={handleSelectionChange}
            restoredSession={
              share?.data
                ? {
                    sessionId: share.data.session_id,
                    conversation: share.data.ui?.conversation || [],
                    csContext: share.data.ui?.cs_context || {},
                  }
                : undefined
            }
          />
        )}
      </div>

      <TicketFeedbackButton ticketId={ticketId} />
    </div>
  );
};

const TicketFeedbackButton = ({ ticketId }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="fixed bottom-6 right-6 z-40 pointer-events-none">
        <button
          type="button"
          className="group flex items-center gap-2 rounded-full border border-gray-300 bg-white text-gray-800 shadow-md pl-1 pr-3 py-1 hover:bg-purple-50 hover:border-ktx-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple transition-colors pointer-events-auto"
          onClick={() => setOpen(true)}
          aria-label="Feedback"
          title="Feedback"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-700 group-hover:text-ktx-purple transition-colors">
            <MessageSquarePlus className="h-5 w-5" aria-hidden />
          </span>
          <span className="text-sm font-medium pr-0.5">Feedback</span>
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Feedback</DialogTitle>
            <DialogDescription>
              Share a bug report or feature request for this dashboard.
            </DialogDescription>
          </DialogHeader>
          <FeedbackForm ticketId={ticketId} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportTicketPage;
